<template>
  <div class="accordion-snippet">
    <div
      v-for="(panel, index) of panels"
      :key="`${panel.handle}-${index}`"
      class="accordion-snippet__panel"
      :class="[{ [$class('active')]: isActive(index) }]"
      :data-handle="panel.handle"
      :activePanels="activePanels"
    >
      <button
        class="accordion-snippet__toggle"
        :class="[{ [$class('active')]: isActive(index) }]"
        type="button"
        :aria-expanded="isActive(index)"
        @click="togglePanel(index, panel.handle)"
      >
        <component
          :is="headingType"
          class="accordion-snippet__heading"
          :class="[headingModifierClasses]"
          v-text="panel.title"
        />
        <icon-chevron-down />
      </button>

      <transition-height>
        <div
          v-show="isActive(index)"
          class="accordion-snippet__content"
          :class="[
            contentModifierClasses,
            { [panel.classes.content]: panel.classes?.content },
          ]"
          v-html="panel.content"
        />
      </transition-height>
    </div>
  </div>
</template>

<script>
/**
 * Vue: Accordion Snippet (accordion-snippet)
 * -----------------------------------------------------------------------------
 * Renders a set of accordion-snippet panels that can be opened and closed.
 *
 * @param {String} [contentModifierClasses] - Space separated classes to modify
 * panel content styling.
 * @param {String} [headingModifierClasses] - Space separated classes to modify
 * the toggle's styling.
 * @param {String} [headingType] - Element type for the toggle label.
 * @param {Array} panelData - Array of panel objects.
 * @param {String} panelData.content - Panel content.
 * @param {String} panelData.title - Title of the panel, used as a label for the
 * toggle button.
 * @param {Object} panelData.classes - Additional classes to use.
 * @param {Boolean} scrollToActive - true/false to scroll to active panel.
 */
import TransitionHeight from '~global/transition-height/transition-height';

import IconChevronDown from '~icons/directional-navigation/chevron/down.svg';

import { convertToHandle } from '~/helpers/convert';

export default {
  name: 'AccordionSnippet',

  components: {
    IconChevronDown,
    TransitionHeight,
  },

  props: {
    contentModifierClasses: {
      type: String,
      default: '',
    },
    headingModifierClasses: {
      type: String,
      default: '',
    },
    headingType: {
      type: String,
      default: 'span',
    },
    panelData: {
      type: Array,
      default: () => [],
    },
    scrollToActive: {
      type: Boolean,
      default: false,
    },
    activePanels: {
      type: String,
      default: '',
    },
  },

  emits: {
    'update-accordion-state': () => true,
  },

  data() {
    return {
      panels: [],
    };
  },

  watch: {
    /**
     * Watch panelData prop to re-map panels data if/when prop updates.
     */
    panelData() {
      this.panels = this.mapPanelData();
    },
  },

  mounted() {
    this.panels = this.mapPanelData();
  },

  methods: {
    /**
     * Check if a panel is active using its index.
     * @param {Number} index - Index of a specific panel.
     * @returns {Boolean}
     */
    isActive(index) {
      return this.panels[index].active;
    },

    /**
     * Store panels on the component with a toggleable 'active' property.
     * - Also adds default title class.
     * @returns {Array}
     */
    mapPanelData() {
      const titleClasses = this.headingModifierClasses
        ? this.headingModifierClasses
        : 'text-mobile-paragraph-m-regular text-desktop-paragraph-m-regular';
      return this.panelData.map((panel, index) => {
        return {
          active: this.activePanels.includes(index),
          classes: {
            title: titleClasses,
          },
          ...panel,
        };
      });
    },

    /**
     * Handleize panel title to use as panel key.
     * @returns {String}
     */
    panelKey(title) {
      return convertToHandle(title);
    },

    /**
     * Toggle a panel's active state.
     * - Opening one panel will close all other panels.
     * @param {Number} targetPanelIndex - Index of a specific panel.
     * @param {String} handle - Updated panel's handle, used to keep panel
     * states synchronised.
     */
    togglePanel(targetPanelIndex, handle) {
      if (this.scrollToActive) {
        const activePanel = document.querySelector(
          '.accordion-snippet__toggle.is-active',
        );
        const headerHeight = document
          .querySelector('header.site-header')
          .getBoundingClientRect().height;

        if (activePanel) {
          const behavior = window.matchMedia('(prefers-reduced-motion: reduce)')
            .matches
            ? 'auto'
            : 'smooth';

          window.scrollTo({
            behavior,
            top:
              activePanel.getBoundingClientRect().top +
              window.scrollY -
              headerHeight,
          });
        }
      }

      this.panels[targetPanelIndex].active =
        !this.panels[targetPanelIndex].active;
      this.$emit('update-accordion-state', {
        active: this.panels[targetPanelIndex].active,
        handle,
      });
    },
  },
};
</script>

<style lang="scss">
@import './accordion-snippet';
</style>
